import { PopperProps } from '@mui/material'
import TooltipIcon from 'ext-ui/components/TooltipIcon/TooltipIcon'
import React from 'react'
import FinalApprovalApprovedIcon from 'src/assets/svgs/FinalApprovalApprovedIcon.svg'
import FinalApprovalFlaggedIcon from 'src/assets/svgs/FinalApprovalFlaggedIcon.svg'
import FinalApprovalPendingIcon from 'src/assets/svgs/FinalApprovalPendingIcon.svg'
import SvgAutoReview from 'src/components/Svgs/SvgAutoReview'
import SvgCheckNew from 'src/components/Svgs/SvgCheckNew'
import SvgClock from 'src/components/Svgs/SvgClock'
import SvgFlagNew from 'src/components/Svgs/SvgFlagNew'
import PolicyStatusTooltipContent from 'src/components/TransactionsDataGrid/cells/PolicyStatusTooltipContent'
import { useTranslate } from 'src/localization/Trns'
import {
  TransactionManagerRejectionReason,
  transactionManagerReviewRejectionReasonToString,
  TransactionManagerReviewStatus,
} from 'src/models/transaction.models'

interface ManagerReviewIconTooltipCellProps {
  status: TransactionManagerReviewStatus | undefined
  rejectionReason?: TransactionManagerRejectionReason
  placement?: PopperProps['placement']
  compact?: boolean
  showReceiptAgentAvatar?: boolean
}

const compactIcon = (src: string): React.ReactElement => (
  <img alt="" height={16} src={src} width={16} />
)

const ManagerReviewIconTooltipCell: React.FC<
  ManagerReviewIconTooltipCellProps
> = ({
  status,
  rejectionReason,
  placement = 'top',
  compact = false,
  showReceiptAgentAvatar = false,
}): React.ReactElement => {
  const translate = useTranslate()

  let icon: React.ReactElement
  let iconTestId: string
  let message: string

  switch (status) {
    case TransactionManagerReviewStatus.Reviewed:
      icon = compact ? compactIcon(FinalApprovalApprovedIcon) : <SvgCheckNew />
      iconTestId = 'reviewedReviewIcon'
      message = translate('Manager Reviewed')
      break
    case TransactionManagerReviewStatus.Rejected: {
      icon = compact ? compactIcon(FinalApprovalFlaggedIcon) : <SvgFlagNew />
      iconTestId = 'flagReviewIcon'
      const reason = translate(
        transactionManagerReviewRejectionReasonToString(rejectionReason),
      )
      message = translate('Manager Rejected: ${reason}', { reason })
      break
    }
    case TransactionManagerReviewStatus.AutoReviewed:
      icon = compact ? (
        <SvgAutoReview height={16} width={16} />
      ) : (
        <SvgAutoReview />
      )
      iconTestId = 'autoReviewIcon'
      message = translate('Auto-reviewed transaction')
      break
    case TransactionManagerReviewStatus.PendingReview:
    default:
      icon = compact ? compactIcon(FinalApprovalPendingIcon) : <SvgClock />
      iconTestId = 'pendingReviewIcon'
      message = translate('Pending Manager Review')
      break
  }

  return (
    <TooltipIcon
      placement={placement}
      icon={
        <span aria-label={message} role="img">
          {icon}
        </span>
      }
      iconTestId={iconTestId}
      disableInteractive={true}
      enterDelay={0}
      exitDelay={0}
    >
      {showReceiptAgentAvatar ? (
        <PolicyStatusTooltipContent>{message}</PolicyStatusTooltipContent>
      ) : (
        message
      )}
    </TooltipIcon>
  )
}

export default ManagerReviewIconTooltipCell
